import { useStyletron } from '@thanx/uikit/theme'
import ChevronDown from 'components/Icons/ChevronDown'
import React from 'react'
import AppIcon from './AppIcon'
import {
  androidBody,
  androidTitle,
  ANDROID_FONT_STACK,
  previewColors,
  singleLineClamp,
} from './previewTheme'
import type { PreviewContent } from './types'

const CARD_RADIUS = '4px'
const CARD_PADDING = '16px'
const ICON_SIZE = '24px'
const ICON_RADIUS = '4px'
const ROW_GAP = '14px'
const PILL_RADIUS = '16px'

export interface AndroidPushCardProps {
  content: PreviewContent
  // The merchant's uploaded logo; falls back to the brand-red placeholder
  // square when omitted (no logo uploaded).
  appIconUrl?: string
}

// Android push notification: a Material collapsed notification (green-tinted
// surface, Roboto, on-surface text, decorative expand-indicator pill) per the
// Figma Android column (430:6259). The {location_name} token renders literally.
const AndroidPushCard = ({ content, appIconUrl }: AndroidPushCardProps) => {
  const [css] = useStyletron()
  return (
    <div
      className={css({
        display: 'flex',
        gap: ROW_GAP,
        alignItems: 'center',
        borderRadius: CARD_RADIUS,
        padding: CARD_PADDING,
        backgroundColor: previewColors.androidSurface,
        fontFamily: ANDROID_FONT_STACK,
      })}
    >
      <AppIcon appIconUrl={appIconUrl} size={ICON_SIZE} radius={ICON_RADIUS} />
      <div
        className={css({
          display: 'flex',
          flexDirection: 'column',
          gap: '4px',
          flex: 1,
          minWidth: 0,
        })}
      >
        <span
          title={content.title}
          className={css({ ...androidTitle, ...singleLineClamp })}
        >
          {content.title}
        </span>
        {/* Collapsed notification truncates the body to a single line. */}
        <span
          title={content.pushText}
          className={css({ ...androidBody, ...singleLineClamp })}
        >
          {content.pushText}
        </span>
      </div>
      {/* Decorative Material expand-indicator pill. */}
      <div
        aria-hidden="true"
        className={css({
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '24px',
          padding: '0 4px',
          borderRadius: PILL_RADIUS,
          backgroundColor: previewColors.androidSurfaceHigh,
          flexShrink: 0,
        })}
      >
        <ChevronDown fillColor={previewColors.androidOnSurfaceVariant} />
      </div>
    </div>
  )
}

export default AndroidPushCard
